import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiExternalLink, FiSearch, FiTrendingUp, FiFileText, FiTarget, FiGlobe, FiAward, FiBarChart3, FiShare2, FiInstagram, FiFacebook, FiLinkedin, FiTwitter, FiCopy, FiCheck, FiMessageCircle, FiImage, FiPlay, FiDownload, FiEye, FiHeart, FiSend, FiChevronDown, FiChevronUp, FiClock, FiZap } = FiIcons;

const ClientView = () => {
  const { shareId } = useParams();
  const { getSharedProject } = useData();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedCaption, setCopiedCaption] = useState(null);
  const [expandedPages, setExpandedPages] = useState(new Set());

  useEffect(() => {
    const fetchProject = async () => {
      const sharedProject = await getSharedProject(shareId);
      setProject(sharedProject);
      setLoading(false);
    };
    fetchProject();
  }, [shareId, getSharedProject]);

  const togglePageExpansion = (pageId) => {
    setExpandedPages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pageId)) {
        newSet.delete(pageId);
      } else {
        newSet.add(pageId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text, pageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCaption(pageId);
      setTimeout(() => setCopiedCaption(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'Facebook': return FiFacebook;
      case 'Instagram': return FiInstagram;
      case 'LinkedIn': return FiLinkedin;
      case 'Twitter': return FiTwitter;
      default: return FiShare2;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'Facebook': return 'from-blue-500 to-blue-600';
      case 'Instagram': return 'from-pink-500 to-purple-600';
      case 'LinkedIn': return 'from-blue-600 to-blue-700';
      case 'Twitter': return 'from-sky-400 to-sky-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getPageStatus = (page) => {
    if (page.ranking) {
      return { status: 'Ranked', color: 'text-green-600', bgColor: 'bg-green-100', icon: FiTrendingUp };
    } else {
      return { status: 'In Progress', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: FiClock };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-red-600 border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-gray-600 text-base sm:text-lg">Loading your SEO report...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <SafeIcon icon={FiGlobe} className="w-10 h-10 sm:w-12 sm:h-12 text-red-600" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Report Not Found</h2>
          <p className="text-gray-600 text-base sm:text-lg">This share link may have expired or is invalid.</p>
        </motion.div>
      </div>
    );
  }

  const totalKeyPhrases = project.pages.reduce((sum, page) => sum + (page.keyPhrases?.length || 0), 0);
  const rankedPages = project.pages.filter(page => page.ranking).length;
  const inProgressPages = project.pages.filter(page => !page.ranking).length;
  const socialMediaReady = project.pages.filter(page => page.social_media_created).length;
  const successRate = project.pages.length > 0 ? Math.round((rankedPages / project.pages.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Enhanced Header with Glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl lg:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-10 lg:mb-12 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-8 w-full lg:w-auto">
              <motion.img
                whileHover={{ scale: 1.05, rotate: 2 }}
                src="https://content.app-sources.com/s/51111663627105881/uploads/Downloaded/Untitled_design-9908745.png"
                alt="Angry Branding"
                className="h-16 w-16 sm:h-18 sm:w-18 lg:h-20 lg:w-20 rounded-xl shadow-lg flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-2 sm:mb-3">
                  {project.website.logo && (
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      src={project.website.logo}
                      alt={project.website.name}
                      className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 rounded-xl object-cover shadow-md flex-shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-transparent break-words">
                      {project.website.name}
                    </h1>
                    <p className="text-gray-600 text-base sm:text-lg lg:text-xl mt-1">SEO Performance Report</p>
                  </div>
                </div>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={project.website.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors bg-blue-50 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base"
                >
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium">Visit Website</span>
                </motion.a>
              </div>
            </div>
            <div className="text-left lg:text-right w-full lg:w-auto">
              <p className="text-sm text-gray-500 mb-1">Report Generated</p>
              <p className="text-lg sm:text-xl font-bold text-gray-900">
                {new Date(project.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Overview Stats with Gradient Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-10 lg:mb-12"
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 relative overflow-hidden col-span-2 sm:col-span-1"
          >
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4 relative z-10">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiFileText} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="font-semibold text-white/90 text-xs sm:text-sm">Total Pages</span>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 relative z-10">{project.pages.length}</p>
            <p className="text-xs sm:text-sm text-white/80 relative z-10">Pages being optimized</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 relative overflow-hidden col-span-2 sm:col-span-1"
          >
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4 relative z-10">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiSearch} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="font-semibold text-white/90 text-xs sm:text-sm">Key Phrases</span>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 relative z-10">{totalKeyPhrases}</p>
            <p className="text-xs sm:text-sm text-white/80 relative z-10">Search terms tracked</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 relative overflow-hidden col-span-2 sm:col-span-1"
          >
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4 relative z-10">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="font-semibold text-white/90 text-xs sm:text-sm">Ranking</span>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 relative z-10">{rankedPages}</p>
            <p className="text-xs sm:text-sm text-white/80 relative z-10">Pages ranking</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 relative overflow-hidden col-span-2 sm:col-span-1"
          >
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4 relative z-10">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiClock} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="font-semibold text-white/90 text-xs sm:text-sm">In Progress</span>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 relative z-10">{inProgressPages}</p>
            <p className="text-xs sm:text-sm text-white/80 relative z-10">Pages optimizing</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 relative overflow-hidden col-span-2 sm:col-span-1"
          >
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4 relative z-10">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiShare2} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="font-semibold text-white/90 text-xs sm:text-sm">Social Ready</span>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 relative z-10">{socialMediaReady}</p>
            <p className="text-xs sm:text-sm text-white/80 relative z-10">Content ready</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-xl lg:rounded-2xl shadow-lg p-3 sm:p-4 lg:p-6 relative overflow-hidden col-span-2 sm:col-span-1"
          >
            <div className="absolute top-0 right-0 w-16 h-16 sm:w-20 sm:h-20 bg-white/10 rounded-full -mr-8 -mt-8 sm:-mr-10 sm:-mt-10" />
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4 relative z-10">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiAward} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="font-semibold text-white/90 text-xs sm:text-sm">Success Rate</span>
            </div>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2 relative z-10">{successRate}%</p>
            <p className="text-xs sm:text-sm text-white/80 relative z-10">Achievement rate</p>
          </motion.div>
        </motion.div>

        {/* Collapsible Pages Grid */}
        <div className="space-y-4 sm:space-y-6">
          {project.pages.map((page, index) => {
            const isExpanded = expandedPages.has(page.id);
            const pageStatus = getPageStatus(page);
            return (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl lg:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 border border-white/20"
              >
                {/* Collapsible Header */}
                <motion.div
                  onClick={() => togglePageExpansion(page.id)}
                  whileHover={{ scale: 1.01 }}
                  className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 flex-1 min-w-0 w-full sm:w-auto">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{page.title}</h3>
                        <span className={`${pageStatus.bgColor} ${pageStatus.color} px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center space-x-1 flex-shrink-0`}>
                          <SafeIcon icon={pageStatus.icon} className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{page.ranking || pageStatus.status}</span>
                        </span>
                        {page.social_media_created && (
                          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <SafeIcon icon={FiShare2} className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between w-full sm:w-auto space-x-4">
                      <div className="flex items-center space-x-3 sm:space-x-6 text-xs sm:text-sm text-gray-600">
                        <div className="flex items-center space-x-1 sm:space-x-2">
                          <SafeIcon icon={FiSearch} className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{page.keyPhrases?.length || 0} phrases</span>
                        </div>
                        <motion.a
                          whileHover={{ scale: 1.05 }}
                          href={page.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 sm:space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SafeIcon icon={FiExternalLink} className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>View Page</span>
                        </motion.a>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-gray-400 flex-shrink-0"
                      >
                        <SafeIcon icon={FiChevronDown} className="w-4 h-4 sm:w-5 sm:h-5" />
                      </motion.div>
                    </div>
                  </div>
                  <p className="text-gray-600 mt-2 sm:mt-3 line-clamp-2 text-sm sm:text-base">{page.content}</p>
                </motion.div>

                {/* Expandable Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 sm:px-6 pb-4 sm:pb-6 border-t border-gray-200">
                        <div className="pt-4 sm:pt-6 space-y-6 sm:space-y-8">
                          {/* Key Phrases */}
                          {page.keyPhrases && page.keyPhrases.length > 0 && (
                            <div>
                              <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center space-x-2 text-sm sm:text-base">
                                <SafeIcon icon={FiSearch} className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                                <span>Target Key Phrases & Search Terms</span>
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {page.keyPhrases.map((keyPhrase, keywordIndex) => (
                                  <motion.span
                                    key={keywordIndex}
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-md"
                                  >
                                    {keyPhrase.phrase}
                                  </motion.span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Meta Description */}
                          {page.meta_description && (
                            <div>
                              <h4 className="font-bold text-gray-900 mb-3 text-sm sm:text-base">Meta Description</h4>
                              <p className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-xl leading-relaxed text-sm sm:text-base">
                                {page.meta_description}
                              </p>
                            </div>
                          )}

                          {/* Social Media Content */}
                          {page.social_media_created && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.2 }}
                              className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl lg:rounded-2xl p-4 sm:p-6 border border-blue-200"
                            >
                              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg lg:rounded-xl flex items-center justify-center">
                                  <SafeIcon icon={FiShare2} className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                </div>
                                <h4 className="font-bold text-gray-900 text-base sm:text-lg">Social Media Content Ready!</h4>
                              </div>

                              {page.social_media_caption && (
                                <div className="mb-4 sm:mb-6">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 space-y-2 sm:space-y-0">
                                    <h5 className="font-semibold text-gray-800 text-sm sm:text-base">Ready-to-Post Caption</h5>
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={() => copyToClipboard(page.social_media_caption, page.id)}
                                      className="flex items-center space-x-2 px-3 py-1 bg-blue-500 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-600 transition-colors self-start sm:self-auto"
                                    >
                                      <SafeIcon icon={copiedCaption === page.id ? FiCheck : FiCopy} className="w-3 h-3 sm:w-4 sm:h-4" />
                                      <span>{copiedCaption === page.id ? 'Copied!' : 'Copy'}</span>
                                    </motion.button>
                                  </div>
                                  <div className="bg-white rounded-xl p-3 sm:p-4 border border-blue-200 shadow-sm">
                                    <pre className="text-gray-700 whitespace-pre-wrap font-sans leading-relaxed text-xs sm:text-sm overflow-x-auto">
                                      {page.social_media_caption}
                                    </pre>
                                  </div>
                                </div>
                              )}

                              {page.social_media_image_url && (
                                <div className="mb-4 sm:mb-6">
                                  <h5 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Social Media Image</h5>
                                  <motion.img
                                    whileHover={{ scale: 1.05 }}
                                    src={page.social_media_image_url}
                                    alt="Social media content"
                                    className="w-full max-w-sm sm:max-w-md rounded-xl shadow-lg cursor-pointer"
                                    onClick={() => window.open(page.social_media_image_url, '_blank')}
                                  />
                                </div>
                              )}

                              {page.social_media_platforms && page.social_media_platforms.length > 0 && (
                                <div className="mb-4 sm:mb-6">
                                  <h5 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Recommended Platforms</h5>
                                  <div className="flex flex-wrap gap-2 sm:gap-3">
                                    {page.social_media_platforms.map((platform, index) => (
                                      <motion.span
                                        key={index}
                                        whileHover={{ scale: 1.05 }}
                                        className={`flex items-center space-x-1 sm:space-x-2 bg-gradient-to-r ${getPlatformColor(platform)} text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg`}
                                      >
                                        <SafeIcon icon={getPlatformIcon(platform)} className="w-3 h-3 sm:w-4 sm:h-4" />
                                        <span>{platform}</span>
                                      </motion.span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {page.social_media_notes && (
                                <div className="mb-4 sm:mb-6">
                                  <h5 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Promotion Strategy</h5>
                                  <p className="text-gray-700 bg-white rounded-xl p-3 sm:p-4 border border-blue-200 leading-relaxed text-sm sm:text-base">
                                    {page.social_media_notes}
                                  </p>
                                </div>
                              )}

                              {/* Action Guide */}
                              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-3 sm:p-4 border border-green-200">
                                <h5 className="font-bold text-green-900 mb-2 sm:mb-3 flex items-center space-x-2 text-sm sm:text-base">
                                  <SafeIcon icon={FiZap} className="w-4 h-4 sm:w-5 sm:h-5" />
                                  <span>Boost Your SEO Rankings</span>
                                </h5>
                                <ul className="space-y-1 sm:space-y-2 text-green-800 text-xs sm:text-sm">
                                  <li className="flex items-center space-x-2">
                                    <SafeIcon icon={FiCheck} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                    <span>Copy the caption and post on {page.social_media_platforms?.join(', ') || 'social media'}</span>
                                  </li>
                                  <li className="flex items-center space-x-2">
                                    <SafeIcon icon={FiCheck} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                    <span>Share with your network and encourage engagement</span>
                                  </li>
                                  <li className="flex items-center space-x-2">
                                    <SafeIcon icon={FiCheck} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 flex-shrink-0" />
                                    <span>Monitor increased traffic to improve search rankings</span>
                                  </li>
                                </ul>
                              </div>
                            </motion.div>
                          )}

                          {/* Performance Metrics */}
                          <div className="grid grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200">
                            <div className="text-center">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg lg:rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3">
                                <SafeIcon icon={FiSearch} className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                              </div>
                              <p className="text-xs sm:text-sm text-gray-500 mb-1">Key Phrases</p>
                              <p className="text-lg sm:text-2xl font-bold text-gray-900">{page.keyPhrases?.length || 0}</p>
                            </div>
                            <div className="text-center">
                              <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r ${page.ranking ? 'from-green-100 to-green-200' : 'from-orange-100 to-orange-200'} rounded-lg lg:rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3`}>
                                <SafeIcon icon={page.ranking ? FiTrendingUp : FiClock} className={`w-5 h-5 sm:w-6 sm:h-6 ${page.ranking ? 'text-green-600' : 'text-orange-600'}`} />
                              </div>
                              <p className="text-xs sm:text-sm text-gray-500 mb-1">Status</p>
                              <p className="text-sm sm:text-lg font-bold text-gray-900">
                                {page.ranking || 'Optimizing'}
                              </p>
                            </div>
                            <div className="text-center">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-100 to-blue-200 rounded-lg lg:rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3">
                                <SafeIcon icon={FiShare2} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                              </div>
                              <p className="text-xs sm:text-sm text-gray-500 mb-1">Social Ready</p>
                              <p className="text-sm sm:text-lg font-bold text-gray-900">
                                {page.social_media_created ? 'Yes' : 'No'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Enhanced Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 sm:mt-16 text-center bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl lg:rounded-3xl shadow-2xl p-8 sm:p-10 lg:p-12 border border-gray-700 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-500/10 to-purple-500/10" />
          <div className="relative z-10">
            <motion.img
              whileHover={{ scale: 1.1, rotate: 5 }}
              src="https://content.app-sources.com/s/51111663627105881/uploads/Downloaded/Untitled_design-9908745.png"
              alt="Angry Branding"
              className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 mx-auto mb-4 sm:mb-6 rounded-xl shadow-lg"
            />
            <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
              Powered by <span className="text-red-400">Angry Branding</span>
            </h3>
            <p className="text-gray-300 text-base sm:text-lg mb-1 sm:mb-2">
              Professional SEO services designed to dominate search results
            </p>
            <p className="text-gray-400 text-sm sm:text-base">
              This report showcases ongoing optimization efforts for your website's search engine performance
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ClientView;